interface MarkerShape {
    width: number;
    height: number;
    shape: string;
}

interface MarkerStyle {
    background: string;
    outline: string;
    text: string;
}

type MarkerType = 'open' | 'closed' | 'mixed' | 'client';

interface MarkerConfig {
    shape: MarkerShape;
    style: MarkerStyle;
}

const markerShapes: Record<string, MarkerShape> = {
    circle: {
        width: 40,
        height: 40,
        shape: '<circle cx="20" cy="20" r="18" fill="@fill" stroke="@stroke" stroke-width="4"/>'
    },
    diamond: {
        width: 48,
        height: 48,
        shape: '<path d="M2.83 24L24 2.83L45.17 24L24 45.17L2.83 24Z" fill="@fill" stroke="@stroke" stroke-width="4"/>'
    },
    triangle: {
        width: 48,
        height: 48,
        shape: '<path d="M 3.6817633,36 24,3.7528744 44.318267,36 Z" fill="@fill" stroke="@stroke" stroke-width="4"/>'
    },
    pin: {
        width: 32,
        height: 40,
        shape: '<path d="M16 37.5558C15.68 37.5558 15.37 37.5058 15.07 37.4058C14.76 37.3058 14.49 37.1458 14.25 36.9158C13.02 35.7858 11.63 34.4458 10.08 32.8758C8.54002 31.3058 7.08002 29.6058 5.72002 27.7658C4.36002 25.9258 3.21002 23.9958 2.29002 21.9658C1.36002 19.9458 0.900024 17.9258 0.900024 15.9058C0.900024 11.3058 2.39002 7.56578 5.38002 4.71578C8.36002 1.85578 11.9 0.425781 16 0.425781C20.1 0.425781 23.63 1.85578 26.62 4.71578C29.61 7.57578 31.1 11.3058 31.1 15.9258C31.1 17.9458 30.64 19.9558 29.71 21.9858C28.78 24.0058 27.64 25.9358 26.29 27.7758C24.94 29.6158 23.49 31.3158 21.94 32.8758C20.4 34.4458 19.01 35.7858 17.77 36.9158C17.53 37.1458 17.25 37.3058 16.94 37.4158C16.63 37.5158 16.31 37.5758 16 37.5758V37.5558Z" fill="@stroke" stroke="@fill" stroke-width="2"/>'
    }
};
const markerStyles: Record<string, MarkerStyle> = {
    green: {
        background: '#1A8914', outline: '#14670F', text: '#ffffff'
    },
    red: {
        background: '#D4111B', outline: '#9F0D14', text: '#ffffff'
    },
    purple: {
        background: '#87189D', outline: '#651276', text: '#ffffff'
    }
};
const markerConfigs: Record<string, MarkerConfig> = {
    open: {
        shape: markerShapes['circle'],
        style: markerStyles['green']
    },
    closed: {
        shape: markerShapes['triangle'],
        style: markerStyles['red']
    },
    mixed: {
        shape: markerShapes['diamond'],
        style: markerStyles['purple']
    },
    client: {
        shape: markerShapes['pin'],
        style: markerStyles['red']
    }
};

export const markerFont = {
    face: 'roboto,arial,sans-serif',
    size: '0.8em'
};

const parser = new DOMParser();

export const markerSvg = {
    make: (
        type: MarkerType,
        label: string,
        ariaLabel: string,
        scale = 1.0,
    ) => {
        if (scale <= 0) {
            throw new Error('Scale must be a positive number');
        }
        const { shape, style } = markerConfigs[type];
        const safeAriaLabel = ariaLabel.replace(/[<>&"']/g, '');
        const safeLabel = label.replace(/[<>&"']/g, '');
        const colouredShape = shape.shape.replace(/@fill/g, style.background).replace(/@stroke/g, style.outline);
        const text = safeLabel.length > 0
            ? `<text transform="scale(${scale})" x="50%" y="50%" style="fill:${style.text}" text-anchor="middle" font-size="${markerFont.size}" dominant-baseline="middle" font-family="${markerFont.face}">${safeLabel}</text>`
            : '';
        return `<svg xmlns="http://www.w3.org/2000/svg" width="${scale * shape.width}" height="${scale * shape.height}" viewBox="0 0 ${shape.width} ${shape.height}" fill="none" role="img" aria-label="${safeAriaLabel}">${colouredShape}${text}</svg>`;
    },
    makeBtoa: (
        type: MarkerType,
        label: string,
        ariaLabel: string,
        scale = 1.0,
    ) => {
        return btoa(markerSvg.make(type, label, ariaLabel, scale));
    },
    makeDomSvg: (
        type: MarkerType,
        label: string,
        ariaLabel: string,
        scale = 1.0,
    ) => {
        return parser.parseFromString(markerSvg.make(type, label, ariaLabel, scale), 'image/svg+xml').documentElement;
    }
};

